import { useEffect, useState } from 'react';
import type { CommsSentPreview } from '@ucp/web/lib';
import { NotSignedInError, useApiFetch } from './useApiFetch';

/**
 * Sent-message preview hook (PRO-0620 follow-up). Fetches the PII-safe, DPIA-gated
 * `GET /comms/sent/:sendId/preview` for a single send when the detail drawer is
 * open. The response `body`/`subject` are the AUTHORED, token-form stored content
 * (merge tokens UNRESOLVED) — never resolved PII / per-recipient content (SEC-8);
 * this hook never derives or stores any. Mirrors `useSentActivity`'s fetch shape
 * (`useApiFetch`, cancel-on-unmount / on-sendId-change), exposing loading / error
 * / ready states so the drawer can render a skeleton, an error, or the preview.
 */
export type SentPreviewStatus = 'loading' | 'ready' | 'error';

export interface SentPreviewState {
  readonly status: SentPreviewStatus;
  readonly preview: CommsSentPreview | null;
  readonly error: string | null;
}

const IDLE: SentPreviewState = { status: 'loading', preview: null, error: null };

export function useSentPreview(sendId: string | null): SentPreviewState {
  const apiFetch = useApiFetch();
  const [state, setState] = useState<SentPreviewState>(IDLE);

  useEffect(() => {
    if (!sendId) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', preview: null, error: null });
    (async () => {
      try {
        const response = await apiFetch(`/comms/sent/${sendId}/preview`);
        if (cancelled || !response) {
          return; // unmounted, or interactive redirect in flight
        }
        if (response.status !== 200) {
          throw new Error(`HTTP ${response.status}`);
        }
        const preview = (await response.json()) as CommsSentPreview;
        if (cancelled) {
          return;
        }
        setState({ status: 'ready', preview, error: null });
      } catch (err) {
        if (cancelled) {
          return;
        }
        setState({
          status: 'error',
          preview: null,
          error:
            err instanceof NotSignedInError
              ? err.message
              : 'Could not load the message preview just now.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiFetch, sendId]);

  return state;
}
